import { useEffect } from "react";
import type { GraphNode } from "../types/graph";
import type { InfrastructureNode } from "../types/pages";

export interface DetailDrawerProps {
  title?: string;
  node?: GraphNode | null;
  onClose: () => void;
  children?: React.ReactNode;
}

function isInfraNode(node: GraphNode): node is InfrastructureNode {
  return "resource_kind" in node;
}

export function DetailDrawer({ title, node, onClose, children }: DetailDrawerProps) {
  const open = !!(node || children);
  const drawerTitle = title ?? (node ? node.name : "Details");

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div className="drawer-overlay" onClick={onClose} aria-hidden="true" />
      <aside className="drawer" role="complementary" aria-label={drawerTitle}>
        <div className="drawer-header">
          <h3 title={drawerTitle}>{drawerTitle}</h3>
          <button onClick={onClose} aria-label="Close drawer">×</button>
        </div>
        <div className="drawer-body">
          {children ?? (node && <NodeProperties node={node} />)}
        </div>
      </aside>
    </>
  );
}

function NodeProperties({ node }: { node: GraphNode }) {
  return (
    <dl>
      <dt>Kind</dt>
      <dd data-testid="detail-kind">{node.kind}</dd>
      <dt>Fully Qualified Name</dt>
      <dd data-testid="detail-fq_name">{node.fq_name}</dd>
      <dt>File Path</dt>
      <dd data-testid="detail-file_path">{node.file_path}</dd>
      <dt>Lines</dt>
      <dd data-testid="detail-start_line">{node.start_line ?? "?"} – {node.end_line ?? "?"}</dd>
      <dt>Language</dt>
      <dd data-testid="detail-language">{node.language ?? "N/A"}</dd>
      <dt>Community ID</dt>
      <dd data-testid="detail-community_id">{node.community_id ?? "N/A"}</dd>
      <dt>Fan In</dt>
      <dd data-testid="detail-fan_in">{node.fan_in}</dd>
      <dt>Fan Out</dt>
      <dd data-testid="detail-fan_out">{node.fan_out}</dd>
      {isInfraNode(node) && (
        <>
          <dt>Resource Kind</dt>
          <dd data-testid="detail-resource_kind">{node.resource_kind}</dd>
          {node.namespace !== undefined && (
            <><dt>Namespace</dt><dd data-testid="detail-namespace">{node.namespace}</dd></>
          )}
          {node.base_image !== undefined && (
            <><dt>Base Image</dt><dd data-testid="detail-base_image">{node.base_image}</dd></>
          )}
          {node.labels !== undefined && (
            <>
              <dt>Labels</dt>
              <dd data-testid="detail-labels">
                {Object.entries(node.labels).map(([k, v]) => (
                  <div key={k}><span className="muted">{k}:</span> {v}</div>
                ))}
              </dd>
            </>
          )}
        </>
      )}
    </dl>
  );
}
